import { useState, useEffect } from "react";

/* ── Resolve platform theme mode ─── */
export function usePlatformThemeMode(platform){
  const mode=platform.platformThemeMode||"dark";
  const [resolved,setResolved]=useState(()=>{
    if(mode!=="system")return mode;
    return window.matchMedia("(prefers-color-scheme:dark)").matches?"dark":"light";
  });
  useEffect(()=>{
    if(mode!=="system"){setResolved(mode);return;}
    const mql=window.matchMedia("(prefers-color-scheme:dark)");
    const h=e=>setResolved(e.matches?"dark":"light");
    setResolved(mql.matches?"dark":"light");
    mql.addEventListener("change",h);
    return()=>mql.removeEventListener("change",h);
  },[mode]);
  return resolved;
}

/* ── Theme class helper: swap -dark suffix to -light ─── */
export function tc(theme,darkClass){
  if(theme==="dark")return darkClass;
  return darkClass.replace(/-dark/g,"-light");
}

/* ── Theme-aware color palette ─── */
export function themeColors(theme){
  return theme==="dark"?{
    text:"white",muted:"rgba(255,255,255,.42)",subtle:"rgba(255,255,255,.2)",
    cardBg:"rgba(255,255,255,.04)",cardBd:"rgba(255,255,255,.09)",
    contentBg:"rgba(255,255,255,.06)",contentBd:"rgba(255,255,255,.08)",contentText:"rgba(255,255,255,.85)",
    footerText:"rgba(255,255,255,.3)",hoverBd:"rgba(255,255,255,.14)",surface:"#08080f",
  }:{
    text:"var(--cink,#1A1714)",muted:"var(--cg500,#6B6660)",subtle:"var(--cg400,#A09A94)",
    cardBg:"var(--cw,white)",cardBd:"var(--cg200,#E8E5E0)",
    contentBg:"var(--cw,white)",contentBd:"var(--cg200,#E8E5E0)",contentText:"var(--cg700,#2E2A26)",
    footerText:"var(--cg400,#A09A94)",hoverBd:"var(--cg300,#D4CFC8)",surface:"var(--cp,#FAFAF9)",
  };
}

/* ── Feature icon SVGs ─── */
export function featureIcon(name,accent){
  const s={width:20,height:20,viewBox:"0 0 20 20",fill:"none",stroke:accent,strokeWidth:"1.5",strokeLinecap:"round",strokeLinejoin:"round"};
  const icons={
    eye:<svg {...s}><circle cx="10" cy="10" r="3"/><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/></svg>,
    zap:<svg {...s}><path d="M11 1L4 12h5l-1 7 7-11h-5l1-7z"/></svg>,
    users:<svg {...s}><circle cx="7" cy="6" r="2.5"/><circle cx="14" cy="7" r="2"/><path d="M1 18c0-3 2.7-5.5 6-5.5s6 2.5 6 5.5"/><path d="M13 12.5c2 0 4 1.5 4 3.5"/></svg>,
    chart:<svg {...s}><rect x="2" y="10" width="3" height="8" rx="1"/><rect x="8.5" y="6" width="3" height="12" rx="1"/><rect x="15" y="2" width="3" height="16" rx="1"/></svg>,
    shield:<svg {...s}><path d="M10 2l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V5l7-3z"/></svg>,
    gear:<svg {...s}><circle cx="10" cy="10" r="3"/><path d="M10 1v2M10 17v2M18 10h-2M4 10H2M15.7 4.3l-1.4 1.4M5.7 14.3l-1.4 1.4M15.7 15.7l-1.4-1.4M5.7 5.7L4.3 4.3"/></svg>,
    home:<svg {...s}><path d="M3 10l7-7 7 7"/><path d="M5 9v7a1 1 0 001 1h3v-4h2v4h3a1 1 0 001-1V9"/></svg>,
    bell:<svg {...s}><path d="M10 18a2 2 0 01-2-2h4a2 2 0 01-2 2zM6 8a4 4 0 018 0c0 4 2 6 2 6H4s2-2 2-6z"/></svg>,
    file:<svg {...s}><path d="M12 2H5a1 1 0 00-1 1v14a1 1 0 001 1h10a1 1 0 001-1V6l-4-4z"/><path d="M12 2v4h4"/></svg>,
    grid:<svg {...s}><rect x="2" y="2" width="6" height="6" rx="1"/><rect x="12" y="2" width="6" height="6" rx="1"/><rect x="2" y="12" width="6" height="6" rx="1"/><rect x="12" y="12" width="6" height="6" rx="1"/></svg>,
    lock:<svg {...s}><rect x="4" y="9" width="12" height="9" rx="1.5"/><path d="M7 9V6a3 3 0 016 0v3"/></svg>,
    globe:<svg {...s}><circle cx="10" cy="10" r="8"/><path d="M2 10h16M10 2c2.5 2.5 4 5 4 8s-1.5 5.5-4 8c-2.5-2.5-4-5-4-8s1.5-5.5 4-8z"/></svg>,
  };
  return icons[name]||<svg {...s}><circle cx="10" cy="10" r="4"/></svg>;
}

/* ── Showcase visual mocks (unique per section) ─── */
export function showcaseVisual(idx,theme,accent,C,title){
  const bd=theme==="dark"?"rgba(255,255,255,.08)":"#E8E5E0";
  const bg2=theme==="dark"?"rgba(255,255,255,.06)":"white";
  const bg3=theme==="dark"?"rgba(255,255,255,.03)":"#F5F4F2";
  const t=(title||"").toLowerCase();
  if(t.includes("dashboard")) return( /* Dashboard: stat cards + bar chart */
    <div style={{background:bg3,borderRadius:14,border:`1px solid ${bd}`,padding:16,height:220}}>
      <div style={{display:"flex",gap:8,marginBottom:12}}>
        {[{l:"Expenses",v:"$12,450"},{l:"Staff",v:"8"},{l:"Properties",v:"3"}].map((s,n)=>(
          <div key={n} style={{flex:1,background:bg2,borderRadius:8,padding:"10px 12px",border:`1px solid ${bd}`}}>
            <div style={{fontSize:10,color:C.muted,marginBottom:4}}>{s.l}</div>
            <div style={{fontSize:14,fontWeight:600,color:C.text}}>{s.v}</div>
          </div>
        ))}
      </div>
      <div style={{background:bg2,borderRadius:8,height:80,border:`1px solid ${bd}`,display:"flex",alignItems:"flex-end",padding:"8px 12px",gap:4}}>
        {[40,55,35,65,45,70,50,60,75,45,55,65].map((h,j)=>(
          <div key={j} className="cmd-chart-bar" style={{flex:1,background:accent,borderRadius:"3px 3px 0 0",height:`${h}%`,opacity:.5+j*.04}}/>
        ))}
      </div>
    </div>
  );
  if(t.includes("automation")) return( /* Automation: IF/THEN rules */
    <div style={{background:bg3,borderRadius:14,border:`1px solid ${bd}`,padding:16,height:220,display:"flex",flexDirection:"column",justifyContent:"center"}}>
      {[{cond:"Maid grocery request",action:"Approve, notify driver"},{cond:"AC filter > 90 days",action:"Schedule maintenance"},{cond:"Spending > SAR 5,000",action:"Send alert to owner"}].map((r,n)=>(
        <div key={n} style={{display:"flex",alignItems:"center",gap:10,marginBottom:n<2?12:0,flexWrap:"wrap"}}>
          <span style={{background:accent+"22",color:accent,fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:4}}>IF</span>
          <span style={{fontSize:13,color:C.text,flex:1,minWidth:120}}>{r.cond}</span>
          <span style={{background:"#D97706"+"22",color:"#D97706",fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:4}}>THEN</span>
          <span style={{fontSize:13,color:C.muted,flex:1,minWidth:120}}>{r.action}</span>
        </div>
      ))}
    </div>
  );
  if(t.includes("whatsapp")) return( /* WhatsApp Connected: expense approval chat */
    <div style={{background:bg3,borderRadius:14,border:`1px solid ${bd}`,padding:16,minHeight:220,display:"flex",flexDirection:"column"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,paddingBottom:8,borderBottom:`1px solid ${bd}`}}>
        <div style={{width:24,height:24,borderRadius:"50%",background:"linear-gradient(135deg,#25D366 0%,#128C7E 100%)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 2px 6px -2px rgba(37,211,102,0.5)"}}>
          <svg width="14" height="14" viewBox="0 0 32 32" fill="white"><path d="M16 3C9.37 3 4 8.37 4 15c0 2.12.55 4.13 1.6 5.92L4 29l8.38-1.55C14 28.13 15 28.4 16 28.4c6.63 0 12-5.37 12-12S22.63 3 16 3zm6.23 16.8c-.27.76-1.56 1.45-2.15 1.54-.56.08-1.27.12-2.05-.13-.47-.15-1.08-.36-1.87-.7-3.28-1.43-5.42-4.76-5.58-4.98-.16-.22-1.32-1.76-1.32-3.36s.83-2.38 1.13-2.71c.3-.33.65-.41.87-.41.22 0 .43 0 .62.01.2.01.47-.07.73.56.27.65.92 2.24 1 2.4.08.17.14.36.03.58-.11.22-.16.35-.33.54-.16.19-.34.42-.49.56-.16.16-.33.33-.14.65.19.32.83 1.37 1.78 2.22 1.23 1.09 2.26 1.43 2.58 1.59.32.16.51.13.7-.08.19-.22.81-.95 1.03-1.27.22-.33.43-.27.73-.16.3.11 1.88.89 2.2 1.05.32.16.54.24.62.38.08.13.08.76-.19 1.52z"/></svg>
        </div>
        <div style={{fontSize:12,fontWeight:600,color:C.text}}>Command Assistant</div>
        <div style={{width:6,height:6,borderRadius:"50%",background:"#25D366",marginInlineStart:"auto"}}/>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",gap:5}}>
        <div style={{display:"flex",justifyContent:"flex-start"}}>
          <div style={{maxWidth:"85%",padding:"7px 10px",borderRadius:"10px 10px 10px 2px",background:bg2,border:`1px solid ${bd}`,fontSize:10,color:C.text,lineHeight:1.4}}>
            New expense: <span style={{fontWeight:700}}>SAR 3,200</span> by <span style={{fontWeight:600}}>Ahmad</span> for <span style={{fontStyle:"italic"}}>AC Maintenance</span>
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-start",gap:6}}>
          <div style={{padding:"5px 14px",borderRadius:8,background:"#2D7D52"+"22",border:`1px solid ${"#2D7D52"}44`,fontSize:10,fontWeight:700,color:"#2D7D52",cursor:"default"}}>Approve</div>
          <div style={{padding:"5px 14px",borderRadius:8,background:"#D94F3D"+"15",border:`1px solid ${"#D94F3D"}33`,fontSize:10,fontWeight:700,color:"#D94F3D",cursor:"default",opacity:.5}}>Reject</div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end"}}>
          <div style={{maxWidth:"60%",padding:"7px 10px",borderRadius:"10px 10px 2px 10px",background:accent+"22",border:`1px solid ${accent}33`,fontSize:10,color:C.text,lineHeight:1.4,fontWeight:600}}>Approve</div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-start"}}>
          <div style={{maxWidth:"85%",padding:"7px 10px",borderRadius:"10px 10px 10px 2px",background:bg2,border:`1px solid ${bd}`,fontSize:10,color:C.text,lineHeight:1.4}}>
            Approved. SAR 3,200 for AC Maintenance logged and Ahmad notified.
          </div>
        </div>
      </div>
    </div>
  );
  if(t.includes("role")||t.includes("account")) return( /* Roles list */
    <div style={{background:bg3,borderRadius:14,border:`1px solid ${bd}`,padding:16,height:220,display:"flex",flexDirection:"column",justifyContent:"center"}}>
      {[{letter:"O",name:"Owner",role:"Full Access",color:accent,grad:"var(--accent-grad)"},{letter:"H",name:"House Manager",role:"Operations",color:"#2D7D52",grad:"linear-gradient(135deg,#2D7D52 0%,#4FB08A 100%)"},{letter:"D",name:"Driver",role:"Schedule Only",color:"#2B5EA7",grad:"linear-gradient(135deg,#2B5EA7 0%,#5A8ECC 100%)"}].map((r,n)=>(
        <div key={n} style={{display:"flex",alignItems:"center",gap:12,marginBottom:n<2?14:0}}>
          <div style={{width:32,height:32,borderRadius:"50%",background:r.grad,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,flexShrink:0,boxShadow:`0 2px 6px -2px ${r.color}80`}}>{r.letter}</div>
          <div style={{flex:1}}><div style={{fontSize:14,fontWeight:600,color:C.text}}>{r.name}</div></div>
          <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:bg2,border:`1px solid ${bd}`,color:C.muted}}>{r.role}</span>
        </div>
      ))}
    </div>
  );
  /* idx===4 or fallback: Monthly trend chart */
  return(
    <div style={{background:bg3,borderRadius:14,border:`1px solid ${bd}`,padding:16,height:220,display:"flex",flexDirection:"column",justifyContent:"center"}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}>
        {["J","F","M","A","M","J","J","A","S","O","N","D"].map((m,n)=>(
          <span key={n} style={{fontSize:10,color:C.subtle}}>{m}</span>
        ))}
      </div>
      <svg viewBox="0 0 240 60" style={{width:"100%",height:60}}>
        <defs>
          <linearGradient id="cmd-chart-line-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#06b6d4"/>
            <stop offset="100%" stopColor="#7c3aed"/>
          </linearGradient>
          <linearGradient id="cmd-chart-area-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.25"/>
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <polygon className="cmd-chart-area" points="0,50 20,45 40,48 60,35 80,30 100,25 120,28 140,20 160,15 180,22 200,18 220,10 240,12 240,60 0,60" fill="transparent"/>
        <polyline className="cmd-chart-line" points="0,50 20,45 40,48 60,35 80,30 100,25 120,28 140,20 160,15 180,22 200,18 220,10 240,12" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <polyline points="0,55 20,52 40,50 60,48 80,45 100,40 120,42 140,38 160,35 180,33 200,30 220,28 240,25" fill="none" stroke={C.muted} strokeWidth="1.5" strokeDasharray="4"/>
      </svg>
    </div>
  );
}
